/**
 * Sprint 88 — ¿Se le puede ofrecer "Cobrar" a este pedido?
 *
 * Vive en `shared` y no dentro de la pantalla por una razón concreta: el Sprint 69 arregló el
 * **servidor** para que un pedido no se cobre dos veces (H-1, crítica) y **dejó el botón vivo en la
 * pantalla**. Su propio comentario lo decía —*"en Pedidos Activos el botón 'Cobrar' seguía
 * apareciendo"*— y ahí se quedó, hasta que el negocio lo reportó:
 *
 * > *"en pedido a domicilio que ya fue pasado a cocina, significa que ya se comprobó el PAGO… pero
 * > en la sección de pedidos se lo vamos a cobrar de nuevo"*.
 *
 * El dinero de la base estaba a salvo: el servidor rechaza el segundo cobro. Lo que no estaba a
 * salvo es el dinero **de la vida real**: un botón que dice "Cobrar" sobre un pedido pagado invita
 * a pedirle otra vez al cliente, y eso pasa antes de que el sistema alcance a decir que no.
 *
 * Al estar aquí, la regla se prueba sola y la comparten escritorio y web.
 */

/** Lo mínimo que hace falta saber del pedido para decidir. */
export interface PedidoParaCobro {
  estado: string;
  /**
   * ¿Ya hay un `Pago` registrado?
   *
   * Se pregunta esto y **no** `estadoPago`, por la misma razón que el servidor: los pedidos de mesa
   * nacen con `estadoPago='pagado'` por compatibilidad histórica, así que ese campo no distingue
   * "ya cobrado" de "no aplica". Mirarlo escondería el botón de cobro de **todas** las mesas.
   */
  yaTienePago?: boolean;
}

/**
 * `true` sólo si tiene sentido ofrecer el cobro.
 *
 * Fail-closed en lo que importa: si el dato de pagos no viene (una vista vieja que aún no lo
 * envía), se comporta como antes y decide sólo por estado — nunca al revés.
 */
export function seLePuedeCobrar(pedido: PedidoParaCobro): boolean {
  if (pedido.estado === 'pagado' || pedido.estado === 'cancelado') return false;
  // Un pedido todavía en captura se cobra desde el propio flujo de venta, no desde la lista.
  if (pedido.estado === 'abierto') return false;
  if (pedido.yaTienePago === true) return false;
  return true;
}

/**
 * Qué decirle a quien mira la fila de un pedido que ya no se puede cobrar porque ya se pagó.
 *
 * El vacío no sirve: la pregunta del operador es *"¿y éste ya lo cobré?"*, y una celda en blanco la
 * deja sin responder. Decirlo es lo que evita que se lo cobre otra vez al cliente.
 */
export function motivoSinCobro(pedido: PedidoParaCobro): 'ya_pagado' | 'cancelado' | null {
  if (pedido.estado === 'cancelado') return 'cancelado';
  if (pedido.estado === 'pagado' || pedido.yaTienePago === true) return 'ya_pagado';
  return null;
}
